import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core'
import { MenuItem } from '../../interfaces/api/MenuItem'
import { NavigationEnd, Router } from '@angular/router'
import { CommonModule } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { WE_LOCALES } from 'app/app.module'
import { MenuItemType } from '../../interfaces/api/MenuItemType'
import { ApiService, UserInfo } from '../../services/api.service'
import { ComponentsModule } from '@components/components.module'
import { MenuService } from '../../services/menu.service'
import { AccountType } from '../../interfaces/api/AccountType'
import { CosentinoSvgComponent } from '@components/cosentino-svg/cosentino-svg.component'
import { CommercialSupportComponent } from '../commercial-support/commercial-support.component'


@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    standalone: true,
    imports: [CommonModule, TranslateModule, ComponentsModule, CosentinoSvgComponent, CommercialSupportComponent]
})
export class SidebarComponent implements OnInit {
    @Input() activeItemCode: string | null;
    @Input() isMobileMenuOpen: boolean;
    @Output() onCloseMobileMenu = new EventEmitter<void>();

    isCommercialSupportOpen: boolean = false;
    showLangPicker: boolean = false;
    userInfo: UserInfo;

    private readonly ctopDisplayCodeByRouteCode: Record<string, string> = {
        'ctop-bath': 'ctop-quoting',
    };

    private readonly ctopRouteCodeByDisplayCode: Record<string, string> = {
        'ctop-quoting': 'ctop-bath',
    };

    get visibleMainMenuItems(): MenuItem[] {
        const items = this.userInfo?.mainMenu?.items || [];

        if (!AccountType.isCtop(this.userInfo?.accountType || null)) {
            return items;
        }

        const itemsByCode = new Map(items.map(item => [item.code, item]));
        const prioritized = ['home', 'ctop-my-projects', 'ctop-bath']
            .map(code => itemsByCode.get(code))
            .filter((item): item is MenuItem => !!item)
            .map(item => this.toDisplayMenuItem(item));

        const removedCodes = new Set(['ctop-bath', 'ctop-kitchen']);
        const prioritizedCodes = new Set(['home', 'ctop-my-projects', 'ctop-bath']);
        const remaining = items
            .filter(item => !removedCodes.has(item.code) && !prioritizedCodes.has(item.code))
            .map(item => this.toDisplayMenuItem(item));

        return [...prioritized, ...remaining];
    }

    get currentLangCode(): string {
        return (this.translate.currentLang || this.translate.getDefaultLang() || 'en').toUpperCase().split('-')[0]
    }

    get languages() {
        return WE_LOCALES.map(code => ({ code, label: code.toUpperCase() }))
    }

    constructor(private router: Router, private api: ApiService, protected menuService: MenuService, private translate: TranslateService) {}

    ngOnInit() {
        this.api.userInfo.subscribe(userInfo => this.userInfo = userInfo);
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                const currentPathCode = event.urlAfterRedirects.split('/')[1] || null;
                this.activeItemCode = this.toDisplayCode(currentPathCode);
                this.isCommercialSupportOpen = false;
            }
        })
    }

    goToProfile() {
        this.router.navigate(['/profile']);
        this.activeItemCode = 'profile';
        if (this.isMobileMenuOpen) {
            this.onCloseMobileMenu.emit();
        }
    }

    logout() {
        this.api.logout();
    }

    changeLang(code: string) {
        this.showLangPicker = false;
        this.translate.use(code);
        localStorage.setItem('we-locale', code);
        window.location.reload();
    }

    goToOtherServices() {
        const menuItem: MenuItem = {
            type: MenuItemType.ROUTE,
            code: 'other-services',
            link: null,
        };
        this.menuService.goTo(menuItem);
        this.activeItemCode = 'other-services';
        if (this.isMobileMenuOpen) {
            this.onCloseMobileMenu.emit();
        }
    }

    onClick(item: MenuItem) {
        const routeCode = this.toRouteCode(item.code);
        this.menuService.goTo({ ...item, code: routeCode });
        if (item.type === MenuItemType.ROUTE) {
            this.activeItemCode = item.code;
        }
        if (this.isMobileMenuOpen) {
            this.onCloseMobileMenu.emit();
        }
    }

    toggleCommercialSupport() {
        this.isCommercialSupportOpen = !this.isCommercialSupportOpen;
    }

    trackByMenuCode(_index: number, item: MenuItem): string {
        return item.code;
    }

    private toDisplayMenuItem(item: MenuItem): MenuItem {
        return {
            ...item,
            code: this.toDisplayCode(item.code) || item.code,
        };
    }

    private toDisplayCode(code: string | null): string | null {
        if (!code || !AccountType.isCtop(this.userInfo?.accountType || null)) {
            return code;
        }
        return this.ctopDisplayCodeByRouteCode[code] || code;
    }

    private toRouteCode(code: string): string {
        if (!AccountType.isCtop(this.userInfo?.accountType || null)) {
            return code;
        }
        return this.ctopRouteCodeByDisplayCode[code] || code;
    }

    protected readonly AccountType = AccountType
}
